import { json } from '@remix-run/node';
import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';

import { getPageBySlug } from '~/providers/pages/pages';

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.page) {
    return [{ title: 'Page Not Found' }];
  }
  const { page } = data;
  const title = page.metaTitle || page.title || 'Untitled Page';
  const description = page.metaDescription || 'Information from our company.';
  return [{ title }, { name: 'description', content: description }];
};

export async function loader({ params, request }: LoaderFunctionArgs) {
  const slug = params.slug;
  if (!slug) throw new Response('Not Found', { status: 404 });
  const page = await getPageBySlug(slug, { request });
  if (!page) throw new Response('Page Not Found', { status: 404 });
  return json({ page });
}

export default function StaticPage() {
  const { page } = useLoaderData<typeof loader>();

  const lastUpdated = new Date(page.updatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white min-h-screen">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-primary to-primary/20 text-white shadow-md">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 text-center">
          <nav className="flex justify-center items-center text-sm font-medium text-white/80 mb-4">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-white font-semibold">{page.title}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight">
            {page.title}
          </h1>
          <p className="mt-4 text-white/80 text-lg">
            Last updated on {lastUpdated}
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="bg-white shadow-lg rounded-3xl p-8 lg:p-12">
          <article
            className="prose prose-lg max-w-full text-slate-700
            prose-h2:text-[#357D8A] prose-h2:font-bold prose-h2:border-l-4 prose-h2:border-[#357D8A] prose-h2:pl-4
            prose-h3:text-[#2a6470] prose-h3:font-semibold prose-h3:mt-10 prose-h3:border-b prose-h3:border-slate-200 prose-h3:pb-2 prose-h3:mb-4
            prose-p:leading-relaxed prose-li:my-2 prose-hr:my-12
            prose-a:text-[#357D8A] prose-a:font-semibold hover:prose-a:text-[#2a6470]
            prose-strong:text-slate-800 prose-blockquote:border-l-4 prose-blockquote:border-[#357D8A] prose-blockquote:text-slate-700
          "
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>

        <div className="mt-16 flex justify-center">
          <Link
            to="/"
            className="inline-block bg-secondary hover:bg-secondary/20 text-white font-medium text-sm px-8 py-3 rounded-full shadow-lg transition-colors duration-300"
          >
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
}
